import { LitElement, css, html } from "lit";

import { customElement, property } from "lit/decorators.js";
import { SocialAuthController } from "ui-common/controllers/social-auth-controller";

@customElement("base-social-button")
export class BaseSocialButton extends LitElement {
  @property() provider = "";
  @property({ type: Boolean }) disabled = false;

  protected socialAuthController = new SocialAuthController(this);
  static override styles = [
    css`
      :host {
        display: flex;
        flex: 1;
        height: 56px;
        background-color: var(--sds-color-background-default-secondary);
        border: var(--sds-size-stroke-border) solid var(--sds-color-border-default-default);
        border-radius: var(--sds-size-radius-600);
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.2s ease;
      }

      :host([disabled]) {
        opacity: 0.6;
        cursor: not-allowed;
      }

      :host([loading]) {
        opacity: 0.8;
        cursor: wait;
      }

      :host([loading])::after {
        content: "";
        position: absolute;
        width: 16px;
        height: 16px;
        margin: auto;
        border: 2px solid transparent;
        border-top-color: currentColor;
        border-radius: 50%;
        animation: spin 1s ease infinite;
      }

      @keyframes spin {
        0% {
          transform: rotate(0deg);
        }
        100% {
          transform: rotate(360deg);
        }
      }

      :host(:hover) {
        background-color: var(--sds-color-background-default-default-hover);
        border-color: var(--sds-color-border-default-secondary);
      }

      :host::slotted(svg) {
        width: var(--sds-size-icon-small);
        height: var(--sds-size-icon-small);
        fill: currentColor;
      }
    `,
  ];

  protected renderContent() {
    return html`<slot></slot>`;
  }

  private async handleClick() {
    if (this.disabled || this.socialAuthController.state.isLoading || !this.provider) return;

    try {
      await this.socialAuthController.authenticateWith(this.provider as "google" | "facebook" | "apple");
    } catch (error) {
      console.error("Social auth error:", error);
      // Error handling is managed by the controller and social auth service
    }
  }

  override connectedCallback(): void {
    super.connectedCallback();
    this.addEventListener("click", this.handleClick.bind(this));
  }

  override render() {
    const isLoading = this.socialAuthController.state.isLoading;

    // Update host attributes for styling
    this.toggleAttribute("loading", isLoading);
    this.toggleAttribute("disabled", this.disabled || isLoading);

    return html` ${this.renderContent()}`;
  }
}
